// Base Imports
import React, { useEffect } from 'react'
import type { GetStaticPropsContext } from 'next'
import dynamic from 'next/dynamic'

// Other Imports
import commerce from '@lib/api/commerce'
import {
  HOMEPAGE_SLUG,
  POLICY_PAGE_DEFAULT_SLUG,
} from '@components/utils/constants'
import withDataLayer, { PAGE_TYPES } from '@components/withDataLayer'
import { EVENTS_MAP } from '@components/services/analytics/constants'
import useAnalytics from '@components/services/analytics/useAnalytics'
import NextHead from 'next/head'
import { useRouter } from 'next/router'
import { IPagePropsProvider } from '@framework/contracts/page-props/IPagePropsProvider'
import { PagePropType, getPagePropType } from '@framework/page-props'
import useEventAnalytics from '@components/services/analytics/useEventAnalytics'
import { AnalyticsEventType } from '@components/services/analytics'
import { useUI } from '@components/ui'

const Layout = dynamic(() => import('@components/common/Layout'))
const Loader = dynamic(() => import('@components/ui/LoadingDots'))

export async function getStaticProps({
  preview,
  locale,
  locales,
}: GetStaticPropsContext) {
  const config = { locale, locales }
  const slugsPromise = commerce.getSlugs({ slug: HOMEPAGE_SLUG })
  const slugs = await slugsPromise
  const infraPromise = commerce.getInfra()
  const infra = await infraPromise

  const pagesPromise = commerce.getAllPages({ config, preview })
  const siteInfoPromise = commerce.getSiteInfo({ config, preview })
  const { pages } = await pagesPromise
  const { categories, brands } = await siteInfoPromise

  // // ---- Display data from content entries ----
  // const id = "05905b59-84a7-41a3-b992-5137c11f86f7";
  // const PageContentsPromise = commerce.getPageContent({ id: id, slug: slugs?.slug });
  // const pageContents = await PageContentsPromise;

  // ---- Display data from pages ----
  //const pageId = "7c79d1b2-53e1-4435-a64f-8298446f3546";
  const PageContentsPromise = commerce.getPagePreviewContent({
    id: '', //pageId,
    slug: POLICY_PAGE_DEFAULT_SLUG, //slugs?.slug,
    workingVersion: process.env.NODE_ENV === 'production' ? true : true, // TRUE for preview, FALSE for prod.
    channel: 'Web',
  })
  const pageContents = await PageContentsPromise
  const props: IPagePropsProvider = getPagePropType({ type: PagePropType.COMMON }) as IPagePropsProvider
  const commonPageProps = await props.getPageProps({ cookies: {} })
  return {
    props: {
      ...commonPageProps,
      categories,
      brands,
      pages,
      slugs,
      globalSnippets: infra?.snippets ?? [],
      snippets: slugs?.snippets ?? [],
      pageContents: pageContents ?? {},
    },
    revalidate: 60,
  }
}
const PAGE_TYPE = PAGE_TYPES.Home
function Privacy({ slugs, pageContents }: any) {
  const { PageViewed } = EVENTS_MAP.EVENT_TYPES
  useAnalytics(PageViewed, {
    entity: JSON.stringify({
      id: slugs?.id,
      name: slugs?.name,
      metaTitle: slugs?.metaTitle,
      MetaKeywords: slugs?.metaKeywords,
      MetaDescription: slugs?.metaDescription,
      Slug: slugs?.slug,
      Title: slugs?.title,
      ViewType: slugs?.viewType,
    }),
    entityName: PAGE_TYPE,
    pageTitle: slugs?.title,
    entityType: 'Page',
    entityId: slugs?.id,
    eventType: 'PageViewed',
  })
  const { recordAnalytics } = useEventAnalytics()
  const { cartItems } = useUI()
  const router = useRouter()
  useEffect(()=>{
    recordAnalytics(AnalyticsEventType.OTHER_PAGE, { type: 'other', data: { cartItems } })
  },[])
  return !pageContents ? (
    <>
      <div className="flex w-full text-center flex-con">
        <Loader />
      </div>
    </>
  ) : (
    <>
      {(pageContents?.metatitle ||
        pageContents?.metadescription ||
        pageContents?.metakeywords) && (
          <>
            <NextHead>
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1, maximum-scale=1"
              />
              <link
                rel="canonical"
                id="canonical"
                href={
                  pageContents?.canonical ||
                  'https://www.damensch.com' + router.asPath
                }
              />

              {pageContents?.metatitle && (
                <>
                  <title>{pageContents?.metatitle}</title>
                  <meta name="title" content={pageContents?.metatitle} />
                </>
              )}

              {pageContents?.metadescription && (
                <meta
                  name="description"
                  content={pageContents?.metadescription}
                />
              )}

              {pageContents?.metakeywords && (
                <meta name="keywords" content={pageContents?.metakeywords} />
              )}
              {pageContents?.metatitle && (
                <meta
                  property="og:title"
                  content={pageContents?.metatitle}
                  key="ogtitle"
                />
              )}

              {pageContents?.metadescription && (
                <meta
                  property="og:description"
                  content={pageContents?.metadescription}
                  key="ogdesc"
                />
              )}
              <meta property="og:type" content="website" />
              <meta property="og:site_name" content="DaMENSCH" key="ogsitename" />
              <meta
                property="og:url"
                content={'https://www.damensch.com'}
                key="ogurl"
              />
              <meta property="og:image" content={pageContents?.image} />
            </NextHead>
          </>
        )}

      <section className="relative pb-10 text-gray-900">
        <div className="container px-3 mx-auto sm:px-6">
          {pageContents?.heading.map((heading: any, hid: number) => (
            <div className="py-6 text-center main-heading" key={hid}>
              <h1 className="text-2xl font-bold text-black sm:text-4xl">
                {heading.heading_title}
              </h1>
            </div>
          ))}
        </div>
        <div className="container flex flex-col w-full px-4 mx-auto mt-2 mb-8 sm:mt-8 sm:px-6">
          <div
            className="p-0 text-sm font-normal text-gray-800 sm:pl-8 sm:pr-8 sm:p-2 custom-html"
            dangerouslySetInnerHTML={{ __html: pageContents.pagecontent }}
          ></div>
        </div>
      </section>
    </>
  )
}

Privacy.Layout = Layout

export default withDataLayer(Privacy, PAGE_TYPE)
